import React, { useContext, useEffect, useState } from 'react';
import {
   Accordion, AccordionBody, AccordionHeader, AccordionItem, Button, ButtonGroup,
   Col, Collapse, Input, List, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table
} from 'reactstrap';
import Switch from "react-switch";
import BarcodeScannerComponent from "react-qr-barcode-scanner";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { BiCheck, BiMinus } from "react-icons/bi";
import _ from 'lodash';
import { ServiceThreadSpinner } from '.';
import { DesktopContainer } from '../layouts';
import { api, constants, dateHelpers, storage, UserContext } from '../utils';

export default function ConsignedTransfer() {
   const ICONS = {
      checkIcon: <BiCheck className="mobileToggleSmall align-self-center text-green" />,
      checkIcon2: <BiCheck className="mobileToggleSmall align-self-center text-greenyellow" />,
      minusIcon: <BiMinus className="mobileToggleSmall align-self-center text-red" />
   }
   const MSG = {
      pick: "Please scan a Pick Ticket barcode from the Shipping Schedule.",
      ship: "Please scan a Case Shipment label.",
      inv: "Please scan a Case Inventory label."
   }
   const { currentUser } = useContext(UserContext);
   const [barcode, setBarcode] = useState(null);
   const [data, setData] = useState({});
   const [expanded, setExpanded] = useState("0");
   const [loaded, setLoaded] = useState(true);
   const [manualEntry, setManual] = useState(null);
   const [msg, setMsg] = useState(MSG.pick);
   const [msgTyp, setMsgTyp] = useState("");
   const [open, setOpen] = useState(null);
   const [printError, setPrintError] = useState(false);
   const [printInfo, setPrintInfo] = useState({});
   const [printPdf, setPrintPdf] = useState(false);
   const [selectCase, setCase] = useState("All");
   const [selectItem, setItem] = useState("All");
   const [showDetail, setShow] = useState(false);
   const [showPrint, setShowPrint] = useState(false);
   const [showReprint, setShowReprint] = useState(false);
   const sleep = ms => new Promise(r => setTimeout(r, ms));
   let modalClassName = currentUser.theme ? `${currentUser.theme}` : "lightTheme";

   let savedDevice = JSON.parse(storage.getItem("deviceType"));
   const isMobile = savedDevice.isMobile;
   const hasScanner = savedDevice.hasScanner;
   const handlePrint = nextChecked => { setPrintPdf(nextChecked); }

   useEffect(() => {
      if (currentUser) {
         inputFocus("searchInput");  // set initial focus on page load
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentUser])

   useEffect(() => {
      // if device has built-in scanner see if a scan was completed
      if (barcode && hasScanner && !manualEntry) {
         setLoaded(false);
      }
      if (manualEntry)
         inputFocus("manualInput");
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [barcode])

   useEffect(() => {
      if (!loaded && barcode) {
         var bcType = constants.GetBarcodeType(barcode);
         if (bcType === constants.BARCODE_TYPE.ConsignedInventory) {
            if (barcode && data && data.transfer && barcode !== data.transfer.pickTicketLabel) {
               confirmAlert({
                  title: "Please Confirm",
                  message: `You have scanned pick ticket ${barcode}. Do you want to switch from ${data.transfer.pickTicketLabel} to ${barcode}?`,
                  buttons: [
                     {
                        label: "Yes",
                        onClick: () => {
                           getPickTicket();
                        },
                     },
                     {
                        label: "No",
                        onClick: () => {
                           inputFocus("searchInput");
                           setLoaded(true);
                        }
                     },
                  ],
               });
            }
            else {
               getPickTicket();
            }
         }

         else {
            if (!data || !data.transfer || data.transfer.pickTicketLabel.length < 1) {
               toast.error(`Invalid Barcode. ${MSG.pick}`);
               setMsg(MSG.pick);
               setLoaded(true);
            }
            else if (data.transfer.barcodeType === constants.CONSIGN_BARCODE_LEN.Shipment) {
               // If a QR code was scanned then parse out just the barcode from the data
               var code = constants.GetRealBarcode(barcode);
               if (code.length !== constants.CONSIGN_BARCODE_LEN.Shipment) {
                  toast.error(`Invalid Barcode. ${MSG.ship}`);
                  return;
               }
               getShipLabel();
            }
            else if (data.transfer.barcodeType === constants.CONSIGN_BARCODE_LEN.Inventory) {
               if (barcode.length === constants.CONSIGN_BARCODE_LEN.Inventory) {
                  if (data.transfer.shipmentLabel.substring(1, 12) !== barcode) {
                     toast.error("This Case Inventory label does not match the current Case Shipment label");
                     setMsg(MSG.inv);
                     setLoaded(true);
                     return;
                  }
                  getInvLabel();
               }
               else {
                  toast.error(`Invalid Barcode. ${MSG.inv}`);
                  setLoaded(true);
               }
            }
            else {
               toast.error("Invalid Barcode Type.");
               setLoaded(true);
            }
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [loaded])

   useEffect(() => {
      if (manualEntry) {
         inputFocus("manualInput");
         return;
      }

      if (!barcode) {
         inputFocus("searchInput");
         return;
      }

      setLoaded(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [manualEntry])

   useEffect(() => {
      if (showDetail) {
         if (data?.transfer?.hasPrintedShipLabels) {
            setMsg(MSG.ship);
         }
         else if (data?.transfer?.hasCases) {
            setMsg(MSG.ship);
            setShowPrint(true);
         }
         else {
            toast.warning('There are no cases to print on this pick ticket.');
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [showDetail])

   useEffect(() => {
      if (loaded && showDetail && printInfo && printInfo.inputType) {
         printShipLabels();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [printInfo])

   function clearState() {
      setShowReprint(false);
      setCase("All");
      setItem("All");
      setBarcode(null);
      setLoaded(true);
      inputFocus("searchInput");
   }

   function close() {
      setShow(false);
      setShowPrint(false);
      setData({});
      setPrintInfo({});
      setMsg(MSG.pick);
      clearState();
   }

   function getInvLabel() {
      api
         .post(`Transfer/ConsignedInventoryLabel/${data.transfer.pickTicketLabel}/${data.transfer.shipmentLabel}/${barcode}`)
         .then(response => {
            if (response.data.success) {
               setData(response.data.model);
               setMsg(MSG.ship);
            }
            else {
               toast.warning(response.data.message);
               //setMsg(MSG.inv);  // should already be this
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
            //setMsg(MSG.inv);    // should already be this
         })
         .finally(() => {
            clearState();
         });
   }

   function getPickTicket() {
      api
         .post(`Transfer/GetConsignTransfer/${barcode}`)
         .then(response => {
            if (response.data.success) {
               setData(response.data.model);
               setPrintInfo(response.data.label);
               setShow(true);
            }
            else {
               toast.error(response.data.message);
               setShow(false);
               setData({});
               setPrintInfo({});
               setMsg(MSG.pick); // not guaranteed, so force it
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
            setShow(false);
            setData({});
            setPrintInfo({});
            setMsg(MSG.pick); // not guaranteed, so force it
         })
         .finally(() => {
            clearState();
         });
   }

   function getShipLabel() {
      // If a QR code was scanned then parse out just the barcode from the data
      var code = constants.GetRealBarcode(barcode);
      api
         .post(`Transfer/ConsignedShipLabel/${data.transfer.pickTicketLabel}/${code}`)
         .then(response => {
            if (response.data.success) {
               setData(response.data.model);
               setMsg(MSG.inv);
            }
            else {
               toast.warning(response.data.message);
               //setMsg(MSG.ship);  // should already be this
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
            //setMsg(MSG.ship);    // should already be this
         })
         .finally(() => {
            clearState();
         });
   }

   const handleKeypress = e => {
      if (e.charCode === 13 || e.key === 'Enter') {
         setManual(null);
         return;
      }
      inputFocus("manualInput");
   };

   async function inputFocus(fieldname) {
      await sleep(80);  // allows time for input to be recognized
      const el = document.querySelector(`input[name=${fieldname}]`);
      if (el !== null) {
         await el.focus();
      }
   }

   async function lookup(val) {
      setRealBarcode(val);
      if (manualEntry) // User entry, not a scan
         inputFocus("manualInput");
      else // Handheld scan
         inputFocus("searchInput");
   }

   function printPackSlip() {
      if (!data.transfer.allItemsScanned) {
         toast.error("All cases must be scanned to print the packing list.");
         return;
      }

      var url = `Transfer/PrintConsignedTransfer/${data.transfer.pickTicketLabel}/${printPdf}`;
      setLoaded(false);
      if (printPdf) {
         api
            .postDownloadBinary(
               url,
               {},
               constants.MIME_TYPES.pdf,
               `${data.transfer.pickTicketLabel}_${dateHelpers.getDateStringForReportName()}.pdf`
            )
            .then((res) => {
               if (res?.data === false) {
                  toast.error(res.message);
               }
            })
            .catch(api.catchHandler)
            .finally(() => {
               close();
            })
      }
      else {
         api
            .post(url)
            .then(response => {
               if (response.data.success) {
                  close();
               }
               else {
                  toast.error(response.data.message);
                  clearState();
               }
            })
            .catch((error) => {
               api.catchHandler(error);
               toast.error(error);
               clearState();
            })
            .finally(() => { setLoaded(true); })
      }
   }

   function printShipChange(bc, type, input, desc) {
      let changes = Object.assign({}, printInfo);
      changes["barcode"] = bc;
      changes["barcodeType"] = type;
      changes["inputType"] = input;
      changes["itemDesc"] = desc;
      changes["printPdf"] = printPdf;
      //changes["printTicketLabel"] = data.transfer.pickTicketLabel;  //should already be set
      setPrintInfo(changes);
   }

   function printShipLabels() {
      setLoaded(false);
      if (printPdf) {
         api
            .postDownloadBinary(
               `Transfer/PrintConsignedLabel`,
               printInfo,
               constants.MIME_TYPES.pdf,
               `${data.transfer.pickTicketLabel}_Labels_${dateHelpers.getDateStringForReportName()}.pdf`
            )
            .then((res) => {
               if (res?.data === false) {
                  toast.error(res.message);
               }
            })
            .catch((error) => {
               setPrintError(true);
               api.catchHandler(error);
               toast.error(error);
            })
            .finally(() => {
               if (msgTyp === "ship") setMsg(MSG.ship);
               refreshTransfer();
            })
      }
      else {
         api
            .post(`Transfer/PrintConsignedLabel`, printInfo)
            .then(response => {
               if (!response.data.success) {
                  toast.warning(response.data.message);
               }
            })
            .catch((error) => {
               setPrintError(true);
               api.catchHandler(error);
               toast.error(error);
            })
            .finally(() => {
               if (msgTyp === "ship") setMsg(MSG.ship);
               refreshTransfer();
            })
      }
   }

   function refreshTransfer() {
      // This should only be called after printShipLabels method completes
      setMsgTyp("");
      if (!printError) {
         //setLoaded(false);
         api
            .post(`Transfer/RefreshConsignedTransfer/${data.transfer.pickTicketLabel}/${printInfo.barcodeType}`)
            .then(response => {
               if (response.data.success) {
                  setData(response.data.model);
               }
               else {
                  toast.error(response.data.message);
               }
            })
            .catch((error) => {
               api.catchHandler(error);
               toast.error(error);
            })
            .finally(() => {
               setShowPrint(false);
               clearState();
            });
      }
      setPrintError(false);
   }

   function reprint() {
      setMsgTyp("");
      if (selectCase !== "All") {
         printShipChange(parseInt(selectCase), data.transfer.barcodeType, 1, "");
      }
      else if (selectItem !== "All") {
         printShipChange(0, data.transfer.barcodeType, 2, selectItem);
      }
      else {
         printShipChange(0, data.transfer.barcodeType, 3, "");
      }
      //printShipLabels("");
   }

   function setRealBarcode(val) {
      // If a QR code was scanned then parse out just the barcode from the data
      var code = constants.GetRealBarcode(val);
      setBarcode(code);
   }

   const toggle = (idx) => {
      if (expanded === idx) {
         setExpanded();
      } else {
         setExpanded(idx);
      }
      inputFocus("searchInput");
   };

   return (
      <>
         <DesktopContainer screenName="Consigned Inventory Transfer">
            <ServiceThreadSpinner on={!loaded}>
               <div>
                  <Row><Col className="h6 fw-bold" xs="12">{msg}</Col></Row>
                  <Row><Col><hr className="mb-0 my-1" /></Col></Row>
                  <br />
                  {!hasScanner && isMobile ?
                     <div>
                        <Row className="justify-content-center">
                           <Col xs="12">
                              <Button
                                 title="scan entry"
                                 className="shredButton w-100 btn-md"
                                 onClick={() => setOpen(1)}>
                                 Scan Barcode
                              </Button>
                           </Col>
                        </Row>
                        <Row className="lineheight8">&nbsp;</Row>
                     </div>
                     : null
                  }
                  <div>
                     <Row className="justify-content-center">
                        <Col xs="12">
                           <Button
                              title="manual entry"
                              className="shredButton w-100 btn-md"
                              onClick={() => setManual(1)}>
                              Manual Entry
                           </Button>
                        </Col>
                     </Row>
                     {isMobile ?
                        <div>
                           <Row className="lineheight8">&nbsp;</Row>
                           <Row className="justify-content-center">
                              <Col xs="12">
                                 <Input
                                    name="searchInput"
                                    placeholder="Focus here to scan barcode"
                                    className="h-100"
                                    onChange={(e) => lookup(e.target.value)}
                                 />
                              </Col>
                           </Row>
                        </div>
                        : null
                     }
                  </div>
                  <br />

                  {data && data.transfer ?
                     <Collapse className="mx-0 py-0 px-0" isOpen={showDetail}>
                        <List type="unstyled">
                           <li key={1} className="fw-bold my-0 px-2">Pick Ticket: {data.transfer.pickTicketLabel}</li>
                           {!data.transfer.hasCases ?
                              <li key={2} className="mobileBlueBg text-white my-0 py-3 px-2">There are no cases to print on this pick ticket.</li>
                              : null
                           }
                        </List>

                        {data.nodeList && data.nodeList.length > 0 ?
                           <div>
                              <Accordion open={expanded} toggle={toggle}>
                                 {_.map(data.nodeList, (item, index) => {
                                    return (
                                       <div details={item} key={index} >
                                          <AccordionItem className={(index % 2 === 0) ? "mobileBlueBg text-white my-0 py-1 px-1" : "mobileBlueBgAlt text-white my-0 py-1 px-1"}>
                                             <AccordionHeader targetId={String(index)}>
                                                {item.image === "minus" ? ICONS.minusIcon : ICONS.checkIcon} {item.itemWithDesc}
                                             </AccordionHeader>
                                             <AccordionBody accordionId={String(index)} className="py-1 px-0">
                                                {item.cases && item.cases.length > 0 ?
                                                   <div>
                                                      {_.map(item.cases, (lot, idx) => {
                                                         return (
                                                            <div details={lot} key={idx} className="py-1 px-0">
                                                               {lot.image === "minus" ? ICONS.minusIcon : ICONS.checkIcon2}  {lot.lot}
                                                            </div>
                                                         )
                                                      })}
                                                   </div>
                                                   : null
                                                }
                                             </AccordionBody>
                                          </AccordionItem>
                                       </div>
                                    )
                                 })}
                              </Accordion>
                           </div>
                           : null
                        }
                     </Collapse>
                     : null
                  }
               </div>

               {data && data.transfer && showDetail ?
                  <div>
                     <Row className="lineheight8">&nbsp;</Row>
                     {data.nodeList && data.nodeList.length > 0 ?
                        <div>
                           {!isMobile ? /* This should only show when on a large device */
                              <div>
                                 <Row>
                                    <Col className="h6 fw-bold" xs="12">
                                       <label>
                                          <span className="my-1">&nbsp; &nbsp; &nbsp; &nbsp; {printPdf ? "Download as PDF" : "Print to printer"}  </span>
                                          <Switch onChange={handlePrint} checked={printPdf} className="react-switch" />
                                       </label>
                                    </Col>
                                 </Row>
                                 <Row className="lineheight8">&nbsp;</Row>
                              </div>
                              : null
                           }
                           <Row>
                              <Col xs="12">
                                 <Button
                                    title="Reprint Labels"
                                    className="shredButton w-100 btn-md my-0"
                                    onClick={() => setShowReprint(true)}>
                                    Reprint Case Labels
                                 </Button>
                              </Col>
                           </Row>
                           <Row className="lineheight8">&nbsp;</Row>
                        </div>
                        : null
                     }
                     {data && data.transfer && data.transfer.allItemsScanned ?
                        <div>
                           <Row>
                              <Col xs="12">
                                 <Button
                                    title="Print Slip"
                                    className="shredButton w-100 btn-md my-0"
                                    onClick={() => printPackSlip()}>
                                    Print Packing List
                                 </Button>
                              </Col>
                           </Row>
                           <Row className="lineheight8">&nbsp;</Row>
                        </div>
                        : null
                     }
                     <Row>
                        <Col xs="12">
                           <Button
                              title="To Search"
                              className="btn w-100 btn-md my-0"
                              onClick={() => close()}>
                              Cancel
                           </Button>
                        </Col>
                     </Row>
                  </div>
                  : null
               }


               <Modal isOpen={manualEntry !== null} contentClassName={modalClassName}>
                  <ModalHeader children="Enter Barcode" />
                  <ModalBody
                     children={
                        <Input
                           name="manualInput"
                           placeholder="Enter barcode"
                           className="h-100"
                           onChange={(e) => lookup(e.target.value)}
                           onKeyPress={handleKeypress}
                        />
                     }
                  />
                  <ModalFooter>
                     <ButtonGroup>
                        <Button
                           children="Retrieve"
                           className="button-success"
                           onClick={() => { setManual(null); }} />
                        <Button
                           children="Cancel"
                           className="button-danger"
                           onClick={() => { setBarcode(null); setManual(null); }} />
                     </ButtonGroup>
                  </ModalFooter>
               </Modal>


               <Modal isOpen={open !== null} contentClassName={modalClassName}>
                  <ModalHeader children="Scan a Barcode" />
                  <ModalBody
                     children={
                        <BarcodeScannerComponent
                           height={350}
                           width={350}
                           onUpdate={(err, result) => {
                              if (result) {
                                 setRealBarcode(result.text);
                                 setOpen(null);
                                 setLoaded(false);
                                 inputFocus("searchInput");
                              }
                              else {
                                 setBarcode(null);
                              }
                           }}
                           // onError={(err) => console.error("error", err)}
                           facingMode={"environment"}
                        />
                     }
                  />
                  <ModalFooter>
                     <Button
                        className="shredButton ms-7"
                        onClick={() => {
                           setBarcode(null);
                           setOpen(null);
                           inputFocus("searchInput");
                        }}>Cancel
                     </Button>
                  </ModalFooter>
               </Modal>


               <Modal isOpen={showPrint} contentClassName={modalClassName}>
                  <ModalHeader className="mobileBlueBg text-white" children="Print Shipping Labels" />
                  <ModalBody className="mobileBlueBgAlt text-white"
                     children={
                        <Table borderless size="sm">
                           <tbody>
                              <tr>
                                 <td className="data-cell my-0 py-0">
                                    <label className="h6 text-white fw-bold my-0">Pick Ticket: {data?.transfer?.pickTicketLabel}</label>
                                 </td>
                              </tr>
                              <tr><td className="data-cell m-0 p-0 lineheight8">&nbsp;</td></tr>
                              {data?.transfer?.hasCases ?
                                 <tr>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-white h6 my-1">If you haven't selected a Label Printer on the Settings page, please do so before printing.</label>
                                    </td>
                                 </tr>
                                 : null
                              }
                              {!data?.transfer?.hasCases ?
                                 <tr>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-red h6 my-1">Unable to print. There are no Cases for this Pick Ticket.</label>
                                    </td>
                                 </tr>
                                 : null
                              }
                              <tr><td className="data-cell m-0 p-0 lineheight8">&nbsp;</td></tr>
                              <tr>
                                 <td className="data-cell my-0 mx-0 p-0">
                                    <Button
                                       className="shredButton w-100 btn-md my-0"
                                       onClick={() => {
                                          setMsgTyp("ship");
                                          printShipChange(0, constants.CONSIGN_BARCODE_LEN.Shipment, 3, "");
                                       }}>
                                       {(data?.transfer?.hasCases) ? "PRINT" : "OK"}
                                    </Button>
                                 </td>
                              </tr>
                              <tr>
                                 <td className="data-cell my-0 mx-0 p-0">
                                    <Button
                                       className="btn w-100 btn-md mt-2 mb-0"
                                       onClick={() => {
                                          setShowPrint(false);
                                          clearState();
                                       }}>
                                       Cancel
                                    </Button>
                                 </td>
                              </tr>
                           </tbody>
                        </Table>
                     }
                  />
               </Modal>


               {data?.transfer ?
                  <Modal isOpen={showReprint !== false} contentClassName={modalClassName}>
                     <ModalHeader className="mobileBlueBg text-white" children="Reprint" />
                     <ModalBody className="mobileBlueBgAlt text-white"
                        children={
                           <Table borderless size="sm">
                              <tbody>
                                 <tr>
                                    <td className="data-cell my-0 py-0">
                                       <label className="h6 text-white fw-bold my-0">Pick Ticket: {data.transfer.pickTicketLabel}</label>
                                    </td>
                                 </tr>
                                 <tr>
                                    <td className="data-cell m-0 p-0 lineheight8">&nbsp;</td>
                                 </tr>
                                 <tr>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-white h6 my-1">Item:</label>
                                    </td>
                                 </tr>
                                 <tr>
                                    <td>
                                       <Input
                                          type="select"
                                          name="itemSelect"
                                          id="itemSelect"
                                          onChange={(e) => setItem(e.target.value)}
                                          value={selectItem}
                                       >
                                          <option key="All" value="All">All Items</option>
                                          {_.map(data.nodeList, (itm, listIdx) => {
                                             return (
                                                <option key="{itm.item}" value={itm.item}>{itm.itemWithDesc}</option>
                                             )
                                          })}
                                       </Input>
                                    </td>
                                 </tr>
                                 <tr>
                                    <td className="data-cell m-0 p-0 lineheight8">&nbsp;</td>
                                 </tr>
                                 <tr>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-white h6 my-1">Case:</label>
                                    </td>
                                 </tr>
                                 <tr>
                                    <td>
                                       <Input
                                          type="select"
                                          name="caseSelect"
                                          id="caseSelect"
                                          onChange={(e) => setCase(e.target.value)}
                                          value={selectCase}
                                       >
                                          <option key="All" value="All">All Cases</option>
                                          {_.map(data.nodeList, (hdr, headIdx) => {
                                             return (
                                                <optgroup key={hdr.itemWithDesc} label={hdr.itemWithDesc}>
                                                   {_.map(hdr.cases, (c, cIdx) => {
                                                      return (
                                                         <option key="{c.lot}" value={c.barcode}>{c.lot}</option>
                                                      )
                                                   })}
                                                </optgroup>
                                             )
                                          })}
                                       </Input>
                                    </td>
                                 </tr>
                                 <tr>
                                    <td className="data-cell m-0 p-0 lineheight8">&nbsp;</td>
                                 </tr>
                                 <tr>
                                    <td className="data-cell my-0 mx-0 p-0">
                                       <Button className="shredButton w-100 btn-md my-0" onClick={() => { reprint(); }}>
                                          PRINT
                                       </Button>
                                    </td>
                                 </tr>
                                 <tr>
                                    <td className="data-cell my-0 mx-0 p-0">
                                       <Button className="btn w-100 btn-md mt-2 mb-0" onClick={() => { clearState(); }}>
                                          Cancel
                                       </Button>
                                    </td>
                                 </tr>
                              </tbody>
                           </Table>
                        }
                     />
                  </Modal>
                  : null
               }

            </ServiceThreadSpinner>
         </DesktopContainer >
      </>
   );
}
